import { useUnmountEffect } from "./useUnmountEffect";
import React, { DOMAttributes, useCallback, useRef } from "react";

interface Callbacks {
  onClick?: () => void;
  onDoubleClick?: () => void;
  onLongPress?: () => void;
  onRightClick?: (e: React.MouseEvent) => void;
}
interface Options {
  suppressMouseMs?: number; // how long to ignore mouse events if touch events are happening
  touchScrollThresholdPx?: number; // threshold to consider something a touch vs a scroll
  clickDelayMs?: number; // Time to wait before determining it's a single click
  longPressDelayMs?: number; // Time held down to trigger long press
}

/**
 * Goals:
 * - support differentiating clicks vs double clicks (mouse event support)
 * - support press vs long press (touch event support)
 *
 * Complexity:
 * - some browsers trigger duplicate mouse events for every touch event due to backwards compatibility at the onset of touch being supported https://w3c.github.io/touch-events/#mouse-events
 * - you can prevent the duplicate mouse event from happening on touch, but doing this also disables scroll which is undesirable (esp for our image grid usage)
 * - A user could use multiple fingers for touching - but for this hook we'll only care about the finger that first touched since it's highly likely that's the event they are intentionally triggering
 * - some browsers expand context menus on long press. We want to suppress that so that only our action occurs
 */
export const useDistinctClickTypes = (
  { onClick, onDoubleClick, onLongPress, onRightClick }: Callbacks,
  {
    suppressMouseMs = 800,
    touchScrollThresholdPx = 10,
    clickDelayMs = 250,
    longPressDelayMs = 500,
  }: Options
): {
  events: Pick<
    DOMAttributes<HTMLElement>,
    | "onTouchStart"
    | "onTouchMove"
    | "onTouchEnd"
    | "onTouchCancel"
    | "onMouseUp"
    | "onContextMenu"
  >;
  suppressContextClassNames: string;
} => {
  const clickTimeout = useRef<NodeJS.Timeout>();
  const longPressTimeout = useRef<NodeJS.Timeout>();
  const longPressed = useRef<boolean>(false);

  const clearClickTimer = (): void => {
    if (clickTimeout.current) {
      clearTimeout(clickTimeout.current);
      clickTimeout.current = undefined;
    }
  };
  const clearLongPressTimer = (): void => {
    if (longPressTimeout.current) {
      clearTimeout(longPressTimeout.current);
      longPressTimeout.current = undefined;
    }
  };

  useUnmountEffect(() => {
    clearClickTimer();
    clearLongPressTimer();
  });

  const touchEventInfo = useRef<
    | {
        touchId?: number;
        startOrigin: { x: number; y: number };
        scrollDetected: boolean;
      }
    | undefined
  >();
  const lastTouchTs = useRef<number>(0);

  // ignore duplicate "ghost" mouse events that get triggered on touch actions
  const shouldIgnoreMouse = useCallback(
    (): boolean => Date.now() - lastTouchTs.current < suppressMouseMs,
    [suppressMouseMs]
  );

  const recordTouch = (): void => {
    lastTouchTs.current = Date.now();
  };

  const isFirstTouch = (e: React.TouchEvent, changed: React.Touch): boolean => {
    const first = e.touches[0];
    return Boolean(first && first.identifier === changed.identifier);
  };

  const onTouchStart = useCallback(
    (e: React.TouchEvent): void => {
      recordTouch();
      const t = e.changedTouches[0];
      // we only care about the first finger for simplicity / practical usage
      if (!t || !isFirstTouch(e, t)) {
        return;
      }
      touchEventInfo.current = {
        touchId: t.identifier,
        startOrigin: {
          x: t.clientX,
          y: t.clientY,
        },
        scrollDetected: false,
      };

      clearLongPressTimer();
      longPressed.current = false;
      longPressTimeout.current = setTimeout(() => {
        longPressed.current = true;
        onLongPress?.();
      }, longPressDelayMs);
    },
    [longPressDelayMs, onLongPress]
  );

  /* TOUCH EVENTS */
  const isScroll = useCallback(
    (t: React.Touch, startOrigin: { x: number; y: number }): boolean => {
      const dx = t.clientX - startOrigin.x;
      const dy = t.clientY - startOrigin.y;
      // if the distance is too far — this is likely a scroll not a tap
      return Math.hypot(dx, dy) > touchScrollThresholdPx;
    },
    [touchScrollThresholdPx]
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent): void => {
      recordTouch();
      const t = e.changedTouches[0];
      if (!t || t.identifier !== touchEventInfo.current?.touchId) {
        return;
      }
      touchEventInfo.current.scrollDetected = isScroll(
        t,
        touchEventInfo.current.startOrigin
      );
      if (touchEventInfo.current.scrollDetected) {
        // User moved finger too far — this is likely a scroll - cancel touch events!
        clearLongPressTimer();
      }
    },
    [isScroll]
  );

  const onTouchEnd = useCallback(
    (e: React.TouchEvent): void => {
      recordTouch();
      const t = e.changedTouches[0];
      if (!t || t.identifier !== touchEventInfo.current?.touchId) {
        return;
      }
      clearLongPressTimer();
      if (!touchEventInfo.current.scrollDetected && !longPressed.current) {
        // Click if: the event isn't a scroll and the long press hasn't already triggered
        onClick?.();
      }
      touchEventInfo.current = undefined;
    },
    [onClick]
  );

  const onTouchCancel = useCallback((e: React.TouchEvent): void => {
    const t = e.changedTouches[0];
    if (!t || t.identifier !== touchEventInfo.current?.touchId) {
      return;
    }
    clearLongPressTimer();
    touchEventInfo.current = undefined;
  }, []);

  /* MOUSE EVENTS */
  const onMouseUp = useCallback(
    (e: React.MouseEvent): void => {
      if (shouldIgnoreMouse() || e.button !== 0) {
        return;
      }
      if (clickTimeout.current) {
        // double click
        clearClickTimer();
        onDoubleClick?.();
      } else {
        // only a single click - with a delay so that we can give the user time to double click
        clickTimeout.current = setTimeout(() => {
          onClick?.();
          clearClickTimer();
        }, clickDelayMs);
      }
    },
    [clickDelayMs, onClick, onDoubleClick, shouldIgnoreMouse]
  );

  /* CONTEXT MENU */
  const onContextMenu = useCallback(
    (e: React.MouseEvent): void => {
      e.preventDefault();
      onRightClick?.(e);
    },
    [onRightClick]
  );

  return {
    events: {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onTouchCancel,
      onMouseUp,
      onContextMenu,
    },
    suppressContextClassNames:
      "user-select-none touch-manipulation ios-no-touch-callout",
  };
};
